import {
  CustomFieldDefinition,
  CustomFieldUsage,
  LabelValues,
  ShareTargetType,
} from '@alucio/aws-beacon-amplify/src/models'
import { DNABox, DNAButton, DNADivider, DNAModal, DNAText, Iffy, luxColors } from '@alucio/lux-ui'
import React, { useMemo } from 'react'
import { FlatList, StyleSheet } from 'react-native'
import { HubORM } from 'src/types/orms'
import { DNAModalVariant } from 'src/components/DNA/Modal/DNAConnectedModal'
import { isEveryoneType, isFilterType, isUserType, ShareFilter, ShareFolderMode } from 'src/components/DNA/Modal/DNAShareFolderModal/DNAShareFolderModal'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import isEqual from 'lodash/isEqual'
import { useIndexedTenantCustomFields } from 'src/state/redux/selector/tenant'

const styles = StyleSheet.create({
  icon: {
    height: 25,
    width: 25,
  },
  hoveredBackgroundColor: {
    backgroundColor: luxColors.hoveredBackground.primary,
  },
  menu: {
    maxHeight: 300,
  },
  titleText: {
    color: colors['color-gray-600'],
    fontSize: 14,
    lineHeight: 16,
  },
  searchBarStyle: {
    backgroundColor: colors['color-text-white'],
    borderColor: luxColors.disabled.secondary,
    borderRadius: 4,
    width: 316,
    overflow: 'hidden',
  },
  searchBarWrapper: {
    marginTop: 20,
    flex: 1,
  },
  recordRow: {
    flex: 1,
    flexDirection: 'row',
    height: 52,
    paddingBottom: 8,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
  },
  noResults: {
    marginLeft: 4,
    marginBottom: 6,
    marginTop: 6,
  },
  resultsWrapperStyle: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  customFieldsText: {
    color: colors['color-gray-300'],
    fontSize: 12,
    lineHeight: 20,
  },
  folderMargins: {
    marginLeft: 24,
    marginRight: 12,
  },
  modal: {
    paddingVertical: 24,
  },
  modalBody: {
    margin: 24,
  },
})

interface PermissionsListProps {
  currentItems: ShareFilter[]
  onRemoveItem: (id: string) => void
  togglePermission: (id: string) => void
}

const PermissionsList: React.FC<PermissionsListProps> = React.memo((props) => {
  const { currentItems, onRemoveItem, togglePermission } = props
  const indexedCustomFields = useIndexedTenantCustomFields({
    usages: { internalUsages: [CustomFieldUsage.USER_FILTER] },
  });

  const items = useMemo(() => {
    const shareFilters: ShareFilter[] = []
    if (currentItems) {
      currentItems.forEach((item) => {
        if (isUserType(item.filter)) {
          shareFilters.push(item)
        }
      })
    }
    shareFilters.sort((a, b) => {
      if (isUserType(a.filter) && isUserType(b.filter)) {
        const aName  = a.filter.givenName + ' ' + a.filter.familyName;
        const bName  = b.filter.givenName + ' ' + b.filter.familyName;
        return aName.toLocaleLowerCase().localeCompare(bName.toLocaleLowerCase());
      }
      return 0;
    })
    if (currentItems) {
      currentItems.forEach((item) => {
        if (isFilterType(item.filter)) {
          shareFilters.unshift(item)
        }
      })
      currentItems.forEach((item) => {
        if (isEveryoneType(item.filter)) {
          shareFilters.unshift(item);
        }
      })
    }
    return shareFilters
  }, [currentItems])

  const ContextMenu: React.FC<{ item: ShareFilter }> = ({ item }) => {
    return (
      <>
        <DNAButton
          appearance="outline"
          status="danger"
          onPress={() => onRemoveItem(item.id)}
          children="Remove"
        />
      </>
    )
  }

  return (
    <FlatList
      data={items.filter((item) => !item.isDeleted)}
      keyExtractor={({ id }) => id}
      ListEmptyComponent={(
        <DNABox>
          <DNAText status="subtle">Only you have access</DNAText>
        </DNABox>
      )}
      ItemSeparatorComponent={() => <DNADivider style={{ marginVertical: 12 }} />}
      scrollEnabled
      renderItem={({ item }) => {
        return (
          <DNABox appearance="row" spacing="between" fill alignY="center">
            {isEveryoneType(item.filter) &&
              <DNABox>
                <DNAText bold colorMode="dark" numberOfLines={1} style={styles.titleText}>
                  Everyone
                </DNAText>
              </DNABox>
            }
            {isUserType(item.filter) &&
              <DNABox testID="sharee-users-container" shrink appearance="col" alignY="center" fill>
                <DNAText bold colorMode="dark" numberOfLines={1} style={styles.titleText}>
                  {`${item.filter.givenName} ${item.filter.familyName}`}
                </DNAText>
                <DNAText style={styles.customFieldsText} numberOfLines={1}>
                  {item.filter.email}
                </DNAText>
              </DNABox>
            }
            {isFilterType(item.filter) &&
              <DNABox appearance={'col'} fill alignY="center">
                {Object.keys(item.filter).map(key => {
                  const fieldDefinition = indexedCustomFields[key];
                  const filters = item.filter[key] as LabelValues
                  const joinedValues = getValuesLabels(fieldDefinition, filters.values).join(', ')
                  return (
                    <DNABox appearance="row" key={key}>
                      <DNAText bold colorMode="dark" numberOfLines={3} style={styles.titleText}>
                        {`${fieldDefinition?.fieldLabel}:`}
                      </DNAText>
                      <DNAText style={[styles.customFieldsText, { marginLeft: 4, width: 300 }]}>{joinedValues}</DNAText>
                    </DNABox>
                  )
                })}
              </DNABox>
            }
            <DNABox appearance="row" alignY="center">
              <ContextMenu item={item} />
            </DNABox>
          </DNABox>
        )
      }}
    />
  )
})

function getValuesLabels(fieldDefinition: CustomFieldDefinition, values: string[]): string[] {
  const labels: string[] = [];
  fieldDefinition?.fieldValueDefinitions.forEach(({ id, value }) => {
    if (values.includes(id)) {
      labels.push(value);
    }
  });

  return labels;
}

interface ShareHubTemplatesProps {
  hubORM: HubORM
  setMode: (mode: ShareFolderMode) => void
  currentItems: ShareFilter[]
  onRemoveItem: (id: string) => void
  onConfirm: () => void
  initialValues: ShareFilter[]
  display: boolean
  isDesktop: boolean
  onShareWithEveryone: () => void
  togglePermission: (id: string) => void
  isProcessing: boolean,
  sendEmailNotification: boolean,
  setSendEmailNotification: (value: boolean) => void,
}

const ShareHubTemplates: DNAModalVariant<ShareHubTemplatesProps> = React.memo((props) => {
  const {
    hubORM,
    toggleModal,
    setMode,
    currentItems,
    onRemoveItem,
    onConfirm,
    onShareWithEveryone,
    initialValues,
    display,
    togglePermission,
    isProcessing,
  } = props

  const canUnshare = initialValues.length > 0 && initialValues.some((item) => !item.isDeleted)
  const unshareOnly = initialValues?.some((item) => !item.isDeleted) && currentItems?.every((item) => item.isDeleted)

  const isSharedWithEveryone = currentItems?.some(({ targetType, isDeleted }) => {
    return targetType === ShareTargetType.ALL && !isDeleted;
  });

  return (
    <DNAModal style={[styles.modal, { display: display ? undefined : 'none' }]}>
      <DNAModal.Header>
        <DNABox
          testID="share-folder-modal-header"
          appearance="row"
          spacing="between"
          style={{ minWidth: 600 }}
          alignY="center"
        >
          <DNABox appearance="row" alignY="center">
            <DNABox appearance="col" alignY="center">
              <DNAText h4>Share Hub Template</DNAText>
              <DNAText
                status="subtle"
                numberOfLines={2}
                style={{ maxWidth: 450 }}
              >
                {hubORM.model.name}
              </DNAText>
            </DNABox>
          </DNABox>
          <DNAButton
            testID="cancel-icon-button"
            appearance="ghost"
            status="subtle"
            iconLeft="x"
            onPress={toggleModal}
          />
        </DNABox>
      </DNAModal.Header>
      <DNAModal.Body style={styles.modalBody}>
        <DNABox appearance="col" fill>
            <DNABox appearance="row" spacing="sm">
              <DNAButton
                testID="add-people-button"
                appearance="outline"
                status="tertiary"
                onPress={() => setMode(ShareFolderMode.ADD_PEOPLE)}
                children="Add people"
              />
              <DNAButton
                testID="share-with-everyone-button"
                appearance="outline"
                status="tertiary"
                onPress={onShareWithEveryone}
                disabled={isSharedWithEveryone}
                children="Share with everyone"
              />
              <DNAButton
                testID="create-access-filter-button"
                appearance="outline"
                status="tertiary"
                onPress={() => setMode(ShareFolderMode.CREATE_ACCESS_FILTER)}
                children="Create access filter"
              />
            </DNABox>
            <DNADivider style={{ marginTop: 24 }} />
            <DNAText label style={{ marginTop: 24 }}>Who Has Access</DNAText>
            <DNABox style={{ marginTop: 16, height: 308 }}>
              <PermissionsList
                currentItems={currentItems}
                onRemoveItem={onRemoveItem}
                togglePermission={togglePermission}
              />
            </DNABox>
        </DNABox>
      </DNAModal.Body>
      {/* FOOTER */}
      <DNAModal.Footer>
        <DNABox appearance="row" spacing="between" style={{ width: '100%' }}>
          <DNABox>
            <Iffy is={canUnshare && !unshareOnly}>
              <DNAButton
                testID="unshare-button"
                appearance="outline"
                onPress={() => setMode(ShareFolderMode.UNSHARE)}
                children="Unshare"
              />
            </Iffy>
          </DNABox>
          <DNABox appearance="row" spacing="sm">
            <DNAButton
              testID="cancel-button"
              appearance="outline"
              status="tertiary"
              onPress={toggleModal}
              children="Cancel"
            />
            <DNAButton
              testID="share-folder-modal-confirmation-button"
              onPress={onConfirm}
              status="primary"
              disabled={isEqual(initialValues, currentItems) || isProcessing}
            >
              {unshareOnly ? 'Unshare' : 'Share'}
            </DNAButton>
          </DNABox>
        </DNABox>
      </DNAModal.Footer>
    </DNAModal>
  )
})

export default ShareHubTemplates
